import { useCallback, useEffect, useMemo, useState } from 'react'
import { backfillFunnel, FUNNEL_SIDE_EVENTS, FUNNEL_STEPS, getFunnel, type FunnelBackfillResult, type RemoteFunnel } from '../lib/adminClient'
import { isMissingEndpoint, LoadError } from '../components/LoadError'
import { num, pct, usd } from '../lib/format'
import { DetailGrid, ExpandableRow, useOpenRow } from '../components/RowDetail'
import { DrillValue } from '../components/Drill'
import { FunnelTrace } from '../components/FunnelTrace'
import type { ExploreRef } from '../lib/adminClient'

// The sign-up to subscription funnel (growth spec section 8), read from
// GET /admin/funnel?from&to. Counts are the server's per event in the range;
// step conversion is each step over the one before it. An event the server
// does not return is shown as "not reported", never as 0: a missing event and
// an event nobody reached are different facts.

const LABEL: Record<string, string> = {
  signup: 'Signed up',
  email_verified: 'Verified email',
  trial_started: 'Trial started',
  first_launch: 'First app launch (usage reporting on)',
  trial_credits_exhausted: 'Used all trial credits',
  checkout_started: 'Started checkout',
  subscription_started: 'Subscribed',
  topup_purchased: 'Top-ups bought',
  referral_signup: 'Referred sign-ups',
  referral_rewarded: 'Referral AI-credit rewards',
  referral_qualified: 'Qualified referrals',
  credit_bank_earned: 'Referral credit earned',
  credit_bank_redeemed: 'Plans paid from credit',
  pending_build_used: 'Demo desk opened in app'
}

const HELP: Record<string, string> = {
  first_launch:
    'From app usage reporting, which is off unless the person turns it on, so this counts only those accounts: a floor, never every first launch.',
  trial_credits_exhausted: 'Not every trial runs out of credits, so this step can be smaller than the next without anything being wrong.',
  pending_build_used: 'Opened the /demo desk after sign-up (Build with AI prefilled).',
  referral_qualified: 'Referred person joined free and verified their email.',
  credit_bank_earned: 'US$12 added to a referrer’s credit bank (capped at US$144 a year).',
  credit_bank_redeemed: 'A month of a plan paid from the credit bank.'
}

// UTC calendar day, YYYY-MM-DD.
function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function daysAgo(n: number): string {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - n)
  return isoDay(d)
}

const PRESETS = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 }
]

/** Summed US$ per event, where the server reports one. */
function funnelUsd(body: RemoteFunnel): Record<string, number> {
  const out: Record<string, number> = {}
  for (const s of body.steps ?? []) if (s && typeof s.usd === 'number') out[s.event] = s.usd
  return out
}

/** How many of each step were recovered from primary records rather than recorded live. */
function funnelRecovered(body: RemoteFunnel): Record<string, number> {
  const out: Record<string, number> = {}
  for (const s of body.steps ?? []) if (s && typeof s.recovered === 'number' && s.recovered > 0) out[s.event] = s.recovered
  return out
}

/** Normalise either response form into event -> count. */
function funnelCounts(body: RemoteFunnel): Record<string, number> {
  const out: Record<string, number> = {}
  if (body.counts) {
    for (const [k, v] of Object.entries(body.counts)) if (typeof v === 'number') out[k] = v
  }
  if (body.steps) {
    for (const s of body.steps) if (s && typeof s.count === 'number') out[s.event] = s.count
  }
  return out
}

type Ready = Extract<State, { s: 'ready' }>

// The events a step counts: the same rows, the same UTC days.
function stepRecords(event: string, state: Ready, recovered = false): ExploreRef {
  return { set: 'funnel', filters: { event, from: state.from, to: state.to, ...(recovered ? { recovered: '1' } : {}) } }
}

// One step opened from the table: what it counts, how much of it was recorded
// live and how much recovered, how many came from the step before and how many
// were lost on the way, and how many went on to the next step.
function StepDetail({ index, state }: { index: number; state: Ready }): JSX.Element {
  const event = FUNNEL_STEPS[index]
  const count = state.counts[event]
  const prevEvent = index > 0 ? FUNNEL_STEPS[index - 1] : null
  const nextEvent = index < FUNNEL_STEPS.length - 1 ? FUNNEL_STEPS[index + 1] : null
  const prev = prevEvent ? state.counts[prevEvent] : undefined
  const next = nextEvent ? state.counts[nextEvent] : undefined
  const recovered = state.recovered[event] ?? 0
  const signups = state.counts.signup
  const label = (e: string): string => LABEL[e] ?? e
  return (
    <DetailGrid
      facts={[
        ['Step', `${index + 1} of ${FUNNEL_STEPS.length}: ${label(event)}`],
        ['Event recorded', <span className="font-mono">{event}</span>],
        ['Range', `${state.from} to ${state.to} (UTC days)`],
        ['Count', count === undefined ? 'Not reported by the server' : num(count)],
        ['Recorded as it happened', count === undefined ? null : num(count - recovered)],
        ['Recovered from primary records', count === undefined ? null : recovered ? num(recovered) : 'None'],
        prevEvent && ['Came from', `${label(prevEvent)}: ${prev === undefined ? 'not reported' : num(prev)}`],
        prevEvent && ['Kept from the step before', count === undefined || prev === undefined ? null : pct(count, prev)],
        prevEvent && [
          'Did not reach this step',
          count === undefined || prev === undefined ? null : prev >= count ? num(prev - count) : `None: ${num(count - prev)} more here than the step before (a step can be reached without the one before it)`
        ],
        index > 0 && ['Of all sign-ups', count === undefined || signups === undefined ? null : pct(count, signups)],
        nextEvent && ['Went on to', `${label(nextEvent)}: ${next === undefined ? 'not reported' : num(next)}${next !== undefined && count ? ` (${pct(next, count)})` : ''}`],
        state.usd[event] !== undefined && ['Money', usd(state.usd[event])]
      ]}
      wide={HELP[event] ? [['What it counts', HELP[event]]] : undefined}
    />
  )
}

type State =
  | { s: 'loading' }
  | { s: 'ready'; counts: Record<string, number>; usd: Record<string, number>; recovered: Record<string, number>; from: string; to: string }
  | { s: 'missing' }
  | { s: 'error'; error: string }

export default function Funnel(): JSX.Element {
  const [from, setFrom] = useState(() => daysAgo(29))
  const [to, setTo] = useState(() => isoDay(new Date()))
  const [state, setState] = useState<State>({ s: 'loading' })
  const rangeError = from && to && from > to ? 'The start date is after the end date.' : null

  const load = useCallback(async (f: string, t: string) => {
    setState({ s: 'loading' })
    const res = await getFunnel(f, t)
    if (res.ok) setState({ s: 'ready', counts: funnelCounts(res.data), usd: funnelUsd(res.data), recovered: funnelRecovered(res.data), from: f, to: t })
    else if (isMissingEndpoint(res.status, res.error)) setState({ s: 'missing' })
    else setState({ s: 'error', error: res.error })
  }, [])

  useEffect(() => {
    void load(from, to)
    // Load once on mount with the default range; later loads are explicit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Steps from before the funnel was recorded, recovered from the accounts'
  // own timestamps and Stripe's sessions (server: funnelBackfill.ts).
  const [recovering, setRecovering] = useState(false)
  const [recovery, setRecovery] = useState<{ ok: true; r: FunnelBackfillResult } | { ok: false; error: string } | null>(null)
  async function recoverHistory(): Promise<void> {
    setRecovering(true)
    const res = await backfillFunnel()
    setRecovering(false)
    setRecovery(res.ok ? { ok: true, r: res.data } : { ok: false, error: res.error })
    if (res.ok) void load(from, to)
  }

  function applyPreset(days: number): void {
    const f = daysAgo(days - 1)
    const t = isoDay(new Date())
    setFrom(f)
    setTo(t)
    void load(f, t)
  }

  const openStep = useOpenRow()
  const top = state.s === 'ready' ? state.counts.signup : undefined
  const rows = useMemo(() => {
    if (state.s !== 'ready') return []
    return FUNNEL_STEPS.map((event, i) => {
      const count = state.counts[event]
      const prevEvent = i > 0 ? FUNNEL_STEPS[i - 1] : null
      const prev = prevEvent ? state.counts[prevEvent] : undefined
      return { event, count, prev }
    })
  }, [state])

  return (
    <div>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Growth</div>
        <h1 className="text-2xl font-semibold mt-1">Funnel</h1>
        <div className="text-sm text-slate-400 mt-1">Sign-up to subscription, by event, for a date range (UTC days, both ends included).</div>
      </div>

      <div className="glass-card rounded-xl p-4 mb-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">From</div>
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            data-testid="funnel-from"
            className="bg-black/30 border border-white/10 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-accent [color-scheme:dark]"
          />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">To</div>
          <input
            type="date"
            value={to}
            min={from}
            onChange={(e) => setTo(e.target.value)}
            data-testid="funnel-to"
            className="bg-black/30 border border-white/10 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-accent [color-scheme:dark]"
          />
        </label>
        <button className="btn-primary" disabled={!!rangeError || !from || !to} onClick={() => void load(from, to)} data-testid="funnel-apply">
          Apply
        </button>
        <div className="flex gap-2 ml-auto">
          {PRESETS.map((p) => (
            <button key={p.days} className="btn-ghost" onClick={() => applyPreset(p.days)}>
              Last {p.label}
            </button>
          ))}
        </div>
        {rangeError && <div className="w-full text-xs text-red-300">{rangeError}</div>}
      </div>

      <div className="glass-card rounded-xl p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="funnel-recovery">
        <div className="text-sm text-slate-300 flex-1 min-w-[16rem]">
          Steps that happened before the funnel was recorded (16 Oct 2026) can be recovered from their own records: each
          account’s sign-up, verification and trial times, and every Stripe checkout. Recovered rows are marked as such.
        </div>
        <button className="btn-ghost" disabled={recovering} onClick={() => void recoverHistory()} data-testid="funnel-recover">
          {recovering ? 'Recovering…' : 'Recover history'}
        </button>
        {recovery && recovery.ok && (
          <div className="w-full text-xs text-slate-300" data-testid="funnel-recovery-result">
            Added {num(recovery.r.signup)} sign-ups, {num(recovery.r.email_verified)} verifications, {num(recovery.r.trial_started)} trials,{' '}
            {num(recovery.r.checkout_started)} checkouts and {num(recovery.r.subscription_started)} subscriptions. Read{' '}
            {num(recovery.r.stripeSessionsSeen)} Stripe sessions; {num(recovery.r.stripeUnmatched)} had no account behind them (test
            probes, or people who never signed up) and were not counted.
            {recovery.r.stripeError && <span className="text-amber-200"> Stripe could not be read: {recovery.r.stripeError}</span>}
          </div>
        )}
        {recovery && !recovery.ok && <div className="w-full text-xs text-red-300">Recovery failed: {recovery.error}</div>}
      </div>

      {state.s === 'loading' && <div className="glass-card rounded-xl p-6 text-sm text-slate-400">Loading funnel…</div>}
      {state.s === 'missing' && (
        <div className="glass-card rounded-xl p-6 text-sm text-amber-200" data-testid="funnel-missing">
          This server has no <code className="text-amber-100">GET /admin/funnel</code> yet (growth spec section 8), so there is no funnel to show.
          Nothing here is estimated from other data.
        </div>
      )}
      {state.s === 'error' && <LoadError what="the funnel" endpoint="GET /admin/funnel" error={state.error} testId="funnel-error" />}

      {state.s === 'ready' && (
        <>
          <FunnelTrace from={state.from} to={state.to} />
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1">Events in the range</div>
          <div className="text-sm text-slate-400 mb-3">Every funnel event recorded in the range, whoever it happened to and whenever they joined.</div>
          <div className="glass-card rounded-xl overflow-hidden mb-6">
            <table className="admin-table" data-testid="funnel-table">
              <thead>
                <tr>
                  <th>Step</th>
                  <th className="text-right">Count</th>
                  <th className="text-right">From previous step</th>
                  <th className="text-right">From sign-up</th>
                  <th className="w-1/3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ event, count, prev }, i) => (
                  <ExpandableRow
                    key={event}
                    open={openStep.isOpen(event)}
                    onToggle={() => openStep.toggle(event)}
                    colSpan={5}
                    testId={`funnel-row-${event}`}
                    detail={() => <StepDetail index={i} state={state} />}
                  >
                    <td>
                      <div className="font-medium text-slate-100">{LABEL[event] ?? event}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{event}</div>
                      {HELP[event] && <div className="text-[11px] text-slate-500 mt-0.5">{HELP[event]}</div>}
                    </td>
                    <td className="text-right tabular-nums text-base" data-testid={`funnel-count-${event}`}>
                      {count === undefined ? (
                        <span className="text-xs text-slate-500">not reported</span>
                      ) : (
                        <DrillValue to={stepRecords(event, state)} label={`${LABEL[event] ?? event}, ${state.from} to ${state.to}`} expect={{ key: 'count', value: count }} testId={`funnel-drill-${event}`}>
                          {num(count)}
                        </DrillValue>
                      )}
                      {state.recovered[event] ? (
                        <div className="text-[10px] text-slate-500" title="Recovered from the primary record, not recorded as it happened" data-testid={`funnel-recovered-${event}`}>
                          <DrillValue to={stepRecords(event, state, true)} label={`${LABEL[event] ?? event} recovered later, ${state.from} to ${state.to}`} expect={{ key: 'count', value: state.recovered[event] }}>
                            {num(state.recovered[event])} recovered
                          </DrillValue>
                        </div>
                      ) : null}
                    </td>
                    <td className="text-right tabular-nums" data-testid={`funnel-step-${event}`}>
                      {i === 0 ? '' : count === undefined || prev === undefined ? '—' : pct(count, prev)}
                    </td>
                    <td className="text-right tabular-nums">
                      {i === 0 ? '' : count === undefined || top === undefined ? '—' : pct(count, top)}
                    </td>
                    <td>
                      {count !== undefined && top ? (
                        <div className="h-2 rounded bg-white/5 overflow-hidden">
                          <div className="h-full bg-accent/70" style={{ width: `${Math.min(100, (count / top) * 100)}%` }} />
                        </div>
                      ) : null}
                    </td>
                  </ExpandableRow>
                ))}
              </tbody>
            </table>
          </div>

          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-3">Alongside the funnel</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="funnel-side">
            {FUNNEL_SIDE_EVENTS.map((event) => {
              const count = state.counts[event]
              return (
                <div key={event} className="glass-card rounded-xl p-4" data-testid={`funnel-side-${event}`}>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">{LABEL[event]}</div>
                  <div className="text-xl font-semibold mt-1 tabular-nums">
                    {count === undefined ? (
                      <span className="text-sm text-slate-500">not reported</span>
                    ) : (
                      <DrillValue to={stepRecords(event, state)} label={`${LABEL[event] ?? event}, ${state.from} to ${state.to}`} expect={{ key: 'count', value: count }} testId={`funnel-drill-${event}`}>
                        {num(count)}
                      </DrillValue>
                    )}
                  </div>
                  {state.usd[event] !== undefined && (
                    <div className="text-xs text-slate-300 mt-1" data-testid={`funnel-usd-${event}`}>
                      <DrillValue to={stepRecords(event, state)} label={`${LABEL[event] ?? event} paid, ${state.from} to ${state.to}`} expect={{ key: 'amount', value: state.usd[event] }}>
                        {usd(state.usd[event])}
                      </DrillValue>
                    </div>
                  )}
                  {event === 'referral_signup' && count !== undefined && state.counts.signup ? (
                    <div className="text-xs text-slate-500 mt-1">{pct(count, state.counts.signup)} of sign-ups</div>
                  ) : null}
                  {HELP[event] && <div className="text-xs text-slate-500 mt-1">{HELP[event]}</div>}
                </div>
              )
            })}
          </div>
          <div className="text-xs text-slate-500 mt-4">
            {state.from} to {state.to}. Counts are events recorded by the server in that range, plus any recovered from
            their primary records, which each step shows separately.
          </div>
        </>
      )}
    </div>
  )
}
